import {UpsertableVenue, upsertDownstreamVenueVerb, VenueUpsertResult} from "../../../rpc/webrpc-client.gen.ts";
import {JSX} from "react";
import {useTranslation} from "react-i18next";
import {BulkUploader} from "../../../upload/bulk-upload.tsx";
import {CompanyCapsule} from "../../../company/capsule.tsx";
import {VenueCapsule} from "../../../venue/capsule.tsx";
import {isEmpty} from "lodash";
import {createFileRoute} from "@tanstack/react-router";
import {Box, Sheet, Stack} from "@mui/joy";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

const downstreamVenueCsvHeaderTransformMap: Record<string, string> = {
    "業者CD": "companyCode",
    "業者名1": "companyName",
    "業者フリガナ": "companySortName",
    "現場CD": "venueCode",
    "現場名1": "venueName",
    "現場フリガナ": "venueSortName",
};

interface CsvDownstreamVenueFood {
    companyCode: string;
    companyName: string;
    companySortName: string;
    venueCode: string;
    venueName: string;
    venueSortName: string;
};

function csvDownstreamVenueFoodToUpsertableVenue(csv: CsvDownstreamVenueFood): UpsertableVenue {
    const rv: UpsertableVenue = {
        companyCode: csv.companyCode,
        companyName: csv.companyName,
        code: csv.venueCode,
        displayName: csv.venueName,
    }
    if (csv.companySortName) {
        rv.companySortName = csv.companySortName;
    }
    if (csv.venueSortName) {
        rv.sortName = csv.venueSortName;
    }
    return rv;
}

function AdminDownstreamVenue(): JSX.Element {
    const {t} = useTranslation();
    const mama = useMothership();
    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <BulkUploader<CsvDownstreamVenueFood, UpsertableVenue, VenueUpsertResult>
                entityTypeLabel={t("entity.downstreamVenue")}
                headerTransformMap={downstreamVenueCsvHeaderTransformMap}
                csvToUpsertFood={csvDownstreamVenueFoodToUpsertableVenue}
                upsertTqo={{
                    mutationKey: [upsertDownstreamVenueVerb],
                    mutationFn: (args) => mama.upsertDownstreamVenue(args)
                        .then(tqrv => tqrv.result),
                }}
                upsertFoodCapsule={(food: UpsertableVenue) =>
                    <Sheet color="primary" variant="soft">
                        <VenueCapsule venue={{displayName: food.displayName, code: food.code}}/>
                    </Sheet>
                }
                existingId={(result) => result.existingId ?? null}
                newId={(result) => result.newId ?? null}
                completedCapsule={({food, color, variant}) => {
                    return <Sheet color={color} variant={variant}>
                        <VenueCapsule venue={{
                            displayName: food.displayName,
                            code: food.code
                        }}/>
                    </Sheet>;
                }}
                conflictCapsules={(tqr: VenueUpsertResult) => {
                    const rv: React.JSX.Element[] = [];
                    if (tqr.conflict) {
                        rv.push(<Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("conflict.requested")}</Box>
                            <VenueCapsule
                                key="venue"
                                venue={{
                                    displayName: tqr.conflict.requested.displayName,
                                    code: tqr.conflict.requested.code
                                }}/>
                            <Box>{t("conflict.existing")}</Box>
                            <VenueCapsule
                                key="venue"
                                venue={{
                                    displayName: tqr.conflict.existing.displayName,
                                    code: tqr.conflict.existing.code
                                }}/>
                        </Stack>);
                    }
                    if (tqr.companyConflict) {
                        rv.push(<Stack direction="row" sx={{alignItems: "center"}}>
                            <Box>{t("conflict.requested")}</Box>
                            <CompanyCapsule key="company"
                                            company={{
                                                displayName: tqr.companyConflict.requested.displayName,
                                                code: tqr.companyConflict.requested.code
                                            }}/>
                            <Box>{t("conflict.existing")}</Box>
                            <CompanyCapsule key="company"
                                            company={{
                                                displayName: tqr.companyConflict.existing.displayName,
                                                code: tqr.companyConflict.existing.code
                                            }}/>
                        </Stack>);
                    }
                    return isEmpty(rv) ? null : rv;
                }}/>
        </PageBody>
    </PageShell>
}

export const Route = createFileRoute("/_auth/admin/downstream-venue")({
    component: AdminDownstreamVenue,
});
